import { useEffect, useState } from "react";
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  type OnChangeFn,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";

import { BackButton } from "@vega-core/components/Buttons/BackButton/BackButton";
import { MobileDrawer } from "@vega-core/components/MobileDrawer/MobileDrawer";
import { useBoolean } from "@vega-core/hooks/useBoolean";
import { m } from "@vega-core/paraglide/messages";

import type { EmptyCardProps } from "../../../Cards/EmptyCard/EmptyCard";
import { type ColumnGroup, type ExpandableRowConfig } from "../../types";

import { MobileTableEmpty } from "./MobileTableEmpty";
import { MobileTableLoading } from "./MobileTableLoading";
import { MobileTableRow } from "./MobileTableRow";

type MobileTableProps<TData, TValue = unknown> = {
  data: TData[];
  columns: ColumnDef<TData, TValue>[];
  columnGroups?: ColumnGroup<TData, TValue>[];
  isLoading?: boolean;
  onRowClick?: (row: TData) => void;
  hasRowClick?: boolean;
  cellClassName?: string;
  wrapperClassName?: string;
  emptyState?: EmptyCardProps;
  paginationElement?: React.ReactNode;
  enableRowSelection?: boolean;
  selectedRows?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  selectOnRowClick?: boolean;
  getCanSelect?: (row: TData) => boolean;
  dataTestId?: string;
  pinnedLeftColumns?: string[];
  expandableConfig?: ExpandableRowConfig<TData>;
  getRowId?: (row: TData, index: number) => string;
  hideUnselectableCheckboxes?: boolean;
  dimUnselectableRowsOnMobile?: boolean;
};

export function MobileTable<TData, TValue = unknown>({
  dataTestId,
  data,
  columns,
  columnGroups,
  isLoading,
  onRowClick,
  hasRowClick,
  cellClassName,
  wrapperClassName,
  emptyState,
  paginationElement,
  enableRowSelection = false,
  selectedRows = {},
  onRowSelectionChange,
  selectOnRowClick = false,
  getCanSelect,
  pinnedLeftColumns: _,
  expandableConfig,
  getRowId,
  hideUnselectableCheckboxes = false,
  dimUnselectableRowsOnMobile = true,
}: MobileTableProps<TData, TValue>) {
  const expandedDrawer = useBoolean(false);
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [rowSelection, setRowSelection] =
    useState<RowSelectionState>(selectedRows);

  useEffect(() => {
    setRowSelection(selectedRows);
  }, [selectedRows]);

  const handleRowSelectionChange: OnChangeFn<RowSelectionState> = (
    updaterOrValue,
  ) => {
    const newRowSelection =
      typeof updaterOrValue === "function"
        ? updaterOrValue(rowSelection)
        : updaterOrValue;

    setRowSelection(newRowSelection);

    if (onRowSelectionChange) {
      onRowSelectionChange(newRowSelection);
    }
  };

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection,
    onRowSelectionChange: handleRowSelectionChange,
    getRowId,
    state: {
      rowSelection,
    },
    renderFallbackValue: "-",
  });

  const handleRowClick = (row: TData, rowId: string) => {
    if (selectOnRowClick && enableRowSelection) {
      const canSelect = getCanSelect ? getCanSelect(row) : true;

      if (canSelect) {
        const isSelected = Boolean(rowSelection[rowId]);
        handleRowSelectionChange({
          ...rowSelection,
          [rowId]: !isSelected,
        });
      }
    }

    if (onRowClick) {
      onRowClick(row);
    }
  };

  const handleExpandRow = (rowId: string) => {
    expandedDrawer.setTrue();
    setExpandedRowId(rowId);
  };

  function renderContent() {
    if (isLoading) {
      return <MobileTableLoading<TData, TValue> columns={columns} />;
    }

    if (data.length === 0 && emptyState) {
      return <MobileTableEmpty emptyState={emptyState} />;
    }

    return (
      <div className="space-y-6">
        {table.getRowModel().rows.map((row) => {
          const isSelected = row.getIsSelected();
          const canSelect = getCanSelect ? getCanSelect(row.original) : true;

          const isExpandedContent =
            (expandableConfig?.getRowLevel(row.original) || 0) > 0;

          if (isExpandedContent) {
            return null;
          }

          return (
            <MobileTableRow<TData, TValue>
              canSelect={canSelect}
              cellClassName={cellClassName}
              columnGroups={columnGroups}
              columns={columns}
              dimUnselectableRows={dimUnselectableRowsOnMobile}
              enableRowSelection={enableRowSelection}
              expandableConfig={expandableConfig}
              handleExpandRow={(rowId: string) => handleExpandRow(rowId)}
              hasRowClick={hasRowClick}
              hideUnselectableCheckboxes={hideUnselectableCheckboxes}
              isSelected={isSelected}
              key={row.id}
              onRowClick={(rowData: TData) => handleRowClick(rowData, row.id)}
              row={row}
              selectOnRowClick={selectOnRowClick}
              table={table}
              wrapperClassName={wrapperClassName}
            />
          );
        })}
      </div>
    );
  }

  const expandedRow = table
    .getRowModel()
    .rows.find(
      (row) => expandableConfig?.getRowId(row.original) === expandedRowId,
    );

  const headercell = expandedRow
    ?.getVisibleCells()
    .find((cell) => cell.column.columnDef.isMobileHeader);

  return (
    <div
      className={wrapperClassName}
      data-testid={dataTestId || "mobile-table"}
    >
      {renderContent()}
      <MobileDrawer
        isOpen={expandedDrawer.value}
        onOpenChange={expandedDrawer.setFalse}
      >
        <div className="flex flex-col gap-6 py-6">
          <BackButton onClick={() => expandedDrawer.setFalse()}>
            {m.btn_back()}
          </BackButton>
          <p className="body-semi-large-semibold">
            {headercell
              ? flexRender(
                  headercell.column.columnDef.cell,
                  headercell.getContext(),
                )
              : null}
          </p>
          {table.getRowModel().rows.map((row) => {
            const isSelected = row.getIsSelected();
            const canSelect = getCanSelect ? getCanSelect(row.original) : true;

            const parentId = expandableConfig
              ? expandableConfig.getParentId?.(row.original)
              : null;

            if (parentId !== expandedRowId) {
              return null;
            }

            return (
              <MobileTableRow<TData, TValue>
                canSelect={canSelect}
                cellClassName={cellClassName}
                columnGroups={columnGroups}
                columns={columns}
                dimUnselectableRows={dimUnselectableRowsOnMobile}
                enableRowSelection={enableRowSelection}
                expandableConfig={expandableConfig}
                handleExpandRow={(rowId: string) => handleExpandRow(rowId)}
                hasRowClick={hasRowClick}
                hideUnselectableCheckboxes={hideUnselectableCheckboxes}
                isSelected={isSelected}
                key={row.id}
                onRowClick={(rowData: TData) => handleRowClick(rowData, row.id)}
                row={row}
                selectOnRowClick={selectOnRowClick}
                table={table}
                wrapperClassName={wrapperClassName}
              />
            );
          })}
        </div>
      </MobileDrawer>

      {paginationElement ? (
        <div
          className="py-4 sm:p-4 w-full flex justify-end border-border/primary"
          data-testid="mobile-table-pagination"
        >
          <div className="w-full " data-testid="mobile-pagination-content">
            {paginationElement}
          </div>
        </div>
      ) : null}
    </div>
  );
}
